import type { either, remoteOption } from '@code-expert/prelude';
import { array, flow, pipe, record, remote, task } from '@code-expert/prelude';
import type { QuizSubmissionPublic, TaskId } from '/imports/domain';
import { isQuizSubmission } from '/imports/domain';
import type { QuestionId, QuizAttemptId, QuizSnapshotId } from '/imports/modules/Quizzes/domain';
import type { QuizApi } from '/imports/modules/Quizzes/shared/presentation/QuizApi';
import { quizApi } from '/imports/modules/Quizzes/shared/presentation/QuizApi';
import type {
  AttemptQuestion,
  UpdateAttemptError,
} from '/imports/modules/Quizzes/solve/shared/domain';
import { invokeProcedure, useCachedQuery, useQuery } from '/imports/ui/hooks/useProcedure';
import { usePublication } from '/imports/ui/hooks/usePublication';
import type { PublicQuiz } from '../../domain/PublicQuiz';

export interface SolveQuizApi extends QuizApi {
  useQuizForAttempt: (
    taskId: TaskId,
    attemptId: QuizAttemptId,
  ) => remoteOption.RemoteOption<PublicQuiz>;

  useAttemptQuestion: (
    attemptId: QuizAttemptId,
    questionId: QuestionId,
  ) => remoteOption.RemoteOption<AttemptQuestion>;

  useTaskSubmissionsSubscription: (taskId: TaskId) => remote.Remote<Array<QuizSubmissionPublic>>;

  updateAttempt: (
    attemptId: QuizAttemptId,
    questionId: QuestionId,
    snapshotId: QuizSnapshotId,
    blankValues: Array<string>,
  ) => Promise<either.Either<UpdateAttemptError, void>>;

  submit: (attemptId: QuizAttemptId) => Promise<void>;
}

export const solveQuizApi: SolveQuizApi = {
  ...quizApi,

  useQuizForAttempt: (taskId, attemptId) => {
    const lastSubmission = usePublication('submissions.forStudent', {
      props: taskId,
      method: 'trigger',
    });
    return useQuery('quizzes.findQuizForAttempt', { props: attemptId }, [lastSubmission])[0];
  },

  useAttemptQuestion: (attemptId, questionId) =>
    useCachedQuery('quizzes.findAttemptQuestion', { props: { attemptId, questionId } })[0],

  useTaskSubmissionsSubscription: (taskId) =>
    pipe(
      usePublication('submissions.forStudent', { props: taskId }),
      remote.map(flow(record.values, array.filter(isQuizSubmission), array.reverse)),
    ),

  updateAttempt: (attemptId, questionId, snapshotId, blankValues) =>
    task.run(
      invokeProcedure('quizzes.updateAttempt')({ attemptId, questionId, snapshotId, blankValues }),
    ),

  submit: (attemptId) => task.run(invokeProcedure('quizzes.submit')(attemptId)),
};
